import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconCircleCheck, IconX } from '@tabler/icons-react'
import { useState } from 'react'

import NycLicenseLookupResult from './NycLicenseLookupResult.component'
import styles from './nyc-license-lookup.module.scss'

import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'

type LookupValues = { findBy: 'license_number' | 'name'; findValue: string }

const NycLicenseLookup = () => {
  const [opened, setOpened] = useState(false)
  const [submitted, setSubmitted] = useState<LookupValues | null>(null)
  const form = useForm<LookupValues>({
    mode: 'controlled',
    initialValues: { findBy: 'license_number', findValue: '' },
    validate: {
      findValue: (value) =>
        value.trim() ? null : 'Enter a license number or name',
    },
    onValuesChange: () => setSubmitted(null),
  })

  const toggle = () => {
    setOpened((value) => !value)
    form.setFieldValue('findValue', '')
    form.clearErrors()
    setSubmitted(null)
  }

  const changeMode = (findBy: LookupValues['findBy']) => {
    form.setValues({ findBy, findValue: '' })
    form.clearErrors()
    setSubmitted(null)
  }

  return (
    <Box pos="relative">
      <Button
        data-testid="nyc-license-lookup-open-button"
        size="sm"
        leftSection={<IconCircleCheck size={18} />}
        onClick={toggle}
        aria-expanded={opened}
        aria-controls="nyc-license-lookup-popup"
      >
        NYC License Lookup
      </Button>
      {opened && (
        <Paper
          id="nyc-license-lookup-popup"
          role="region"
          aria-label="NYC License Lookup"
          data-testid="nyc-license-lookup-popup-card"
          className={styles.popup}
          withBorder
          shadow="md"
          p="md"
        >
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={700}>NYC License Lookup</Text>
              <ActionIcon
                data-testid="nyc-license-lookup-close-button"
                aria-label="Close NYC License Lookup"
                variant="subtle"
                onClick={toggle}
              >
                <IconX size={18} />
              </ActionIcon>
            </Group>
            <Text size="sm">
              Lookup the users license information on{' '}
              <Anchor
                data-testid="nyc-license-lookup-open-data-link"
                href="https://data.cityofnewyork.us/Transportation/For-Hire-Vehicles-FHV-Active-Drivers/xjfq-wh2d/data"
                target="_blank"
                rel="noreferrer"
              >
                NYC OpenData
              </Anchor>
            </Text>
            <Group grow gap={0}>
              <Button
                data-testid="nyc-license-lookup-license-button"
                variant={
                  form.values.findBy === 'license_number' ? 'filled' : 'light'
                }
                aria-pressed={form.values.findBy === 'license_number'}
                onClick={() => changeMode('license_number')}
              >
                By License
              </Button>
              <Button
                data-testid="nyc-license-lookup-name-button"
                variant={form.values.findBy === 'name' ? 'filled' : 'light'}
                aria-pressed={form.values.findBy === 'name'}
                onClick={() => changeMode('name')}
              >
                By Name
              </Button>
            </Group>
            <form
              onSubmit={form.onSubmit((values) =>
                setSubmitted({ ...values, findValue: values.findValue.trim() }),
              )}
            >
              <Stack gap="sm">
                <TextInput
                  data-testid="nyc-license-lookup-query-input"
                  aria-label={
                    form.values.findBy === 'license_number'
                      ? 'License number'
                      : 'Driver name'
                  }
                  placeholder={
                    form.values.findBy === 'license_number'
                      ? 'Enter License Number'
                      : 'Enter Name'
                  }
                  type={
                    form.values.findBy === 'license_number' ? 'number' : 'text'
                  }
                  errorProps={{
                    'data-testid': 'nyc-license-lookup-validation-label',
                  }}
                  {...form.getInputProps('findValue')}
                />
                <Button
                  data-testid="nyc-license-lookup-search-button"
                  type="submit"
                >
                  Search
                </Button>
              </Stack>
            </form>
            {submitted && <NycLicenseLookupResult {...submitted} />}
          </Stack>
        </Paper>
      )}
    </Box>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  NycLicenseLookup,
  'is_nyc_lookup_enabled',
)
